import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';

type CareRequest = {
  id: string;
  user_id: string;
  status: 'pending' | 'matched' | 'completed' | 'cancelled';
  care_start_date: string;
  care_end_date: string;
  care_region: string;
  hospital_name: string | null;
  home_address: string | null;
  patient_name: string | null;
  patient_age: number | null;
  patient_gender: string | null;
  disease_type: string | null;
  care_type: string | null;
  additional_notes: string | null;
  daily_hours: number | null;
  hourly_budget: number | null;
  created_at: string;
  updated_at: string;
};

export default function MyCareRequestsPage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const [requests, setRequests] = useState<CareRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.userId) return;
    loadRequests();
  }, [auth.userId]);

  async function loadRequests() {
    if (!auth.userId) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: queryError } = await supabase
        .from('patient_care_requests')
        .select('*')
        .eq('user_id', auth.userId)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;
      setRequests((data || []) as CareRequest[]);
    } catch (err) {
      console.error('Failed to load care requests:', err);
      setError('간병 요청 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel(requestId: string) {
    if (!confirm('이 간병 요청을 취소하시겠습니까? 취소 후에는 되돌릴 수 없습니다.')) return;
    setCancellingId(requestId);
    try {
      const { error: updateError } = await supabase
        .from('patient_care_requests')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('id', requestId);

      if (updateError) throw updateError;

      setRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: 'cancelled' as const } : r))
      );
    } catch (err) {
      console.error('Failed to cancel request:', err);
      setError('요청 취소 중 오류가 발생했습니다.');
    } finally {
      setCancellingId(null);
    }
  }

  function getStatusBadge(status: string) {
    const styles: Record<string, { bg: string; text: string; label: string }> = {
      pending: { bg: 'bg-amber-50', text: 'text-amber-600', label: '매칭 대기중' },
      matched: { bg: 'bg-green-50', text: 'text-green-600', label: '매칭 완료' },
      completed: { bg: 'bg-blue-50', text: 'text-blue-600', label: '간병 완료' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: '취소됨' },
    };
    const s = styles[status] || styles.pending;
    return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.bg} ${s.text}`}>{s.label}</span>;
  }

  function formatDate(dateStr: string | null) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate('/app/patient/home')}
            className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-line text-gray-500" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-extrabold text-gray-900">내 간병 요청</h1>
            <p className="text-xs text-gray-400">내가 등록한 간병 요청 목록입니다</p>
          </div>
        </div>

        {/* 에러 */}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 text-xs text-red-500 text-center">{error}</div>
        )}

        {/* 요청 목록 */}
        {requests.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-file-list-3-line text-xl text-gray-300" />
            </div>
            <p className="text-sm font-medium text-gray-400">등록된 간병 요청이 없습니다</p>
            <p className="text-xs text-gray-300 mt-1">간병 요청을 등록하고 간병인을 찾아보세요</p>
            <button
              onClick={() => navigate('/app/care-request')}
              className="mt-4 px-5 py-2.5 rounded-xl text-xs font-bold text-white cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              <i className="ri-add-line mr-1" />
              간병 요청 등록하기
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((req) => (
              <div key={req.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="p-4">
                  {/* 상태 + 제목 */}
                  <div className="flex items-center justify-between mb-3">
                    {getStatusBadge(req.status)}
                    <span className="text-[10px] text-gray-400">
                      등록일: {formatDate(req.created_at)}
                    </span>
                  </div>

                  {/* 환자 정보 */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-[#E8F5E9] flex items-center justify-center shrink-0">
                      <i className="ri-hospital-line text-lg text-[#2E7D6B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-gray-900">
                          {req.patient_name || '환자'}
                        </h3>
                        {req.patient_age && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-500 font-medium">
                            {req.patient_gender} · {req.patient_age}세
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                        <i className="ri-map-pin-line text-gray-400 text-[10px]" />
                        {req.care_region || req.hospital_name || req.home_address || '지역 미입력'}
                      </p>
                    </div>
                  </div>

                  {/* 요청 상세정보 그리드 */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 bg-gray-50 rounded-xl p-3 mb-3">
                    {req.care_start_date && (
                      <div>
                        <span className="text-[10px] text-gray-400 block">간병 기간</span>
                        <span className="text-xs font-bold text-gray-800">
                          {formatDate(req.care_start_date)} ~ {formatDate(req.care_end_date)}
                        </span>
                      </div>
                    )}
                    {req.daily_hours && (
                      <div>
                        <span className="text-[10px] text-gray-400 block">하루 시간</span>
                        <span className="text-xs font-bold text-gray-800">{req.daily_hours}시간</span>
                      </div>
                    )}
                    {req.hourly_budget && (
                      <div>
                        <span className="text-[10px] text-gray-400 block">희망 시급</span>
                        <span className="text-xs font-bold text-gray-800">{req.hourly_budget.toLocaleString()}원</span>
                      </div>
                    )}
                    {req.care_type && (
                      <div>
                        <span className="text-[10px] text-gray-400 block">간병 유형</span>
                        <span className="text-xs font-bold text-gray-800">{req.care_type}</span>
                      </div>
                    )}
                    {req.hospital_name && (
                      <div className="col-span-2">
                        <span className="text-[10px] text-gray-400 block">병원/시설</span>
                        <span className="text-xs font-bold text-gray-800">{req.hospital_name}</span>
                      </div>
                    )}
                    {req.disease_type && (
                      <div className="col-span-2">
                        <span className="text-[10px] text-gray-400 block">질환</span>
                        <span className="text-xs inline-block px-2 py-0.5 rounded-full bg-orange-50 text-[#F57C00] font-medium mt-0.5">
                          {req.disease_type}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* 추가 메모 */}
                  {req.additional_notes && (
                    <div className="bg-amber-50 rounded-xl p-3 mb-3">
                      <p className="text-[10px] font-bold text-amber-600 mb-1 flex items-center gap-1">
                        <i className="ri-sticky-note-line" /> 추가 요청사항
                      </p>
                      <p className="text-xs text-gray-700 leading-relaxed">{req.additional_notes}</p>
                    </div>
                  )}

                  {/* 액션 버튼 */}
                  {req.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate('/app/patient/search')}
                        className="flex-1 py-2.5 rounded-xl text-xs font-bold text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
                        style={{ backgroundColor: '#2E7D6B' }}
                      >
                        <i className="ri-search-line mr-1" />
                        간병인 찾기
                      </button>
                      <button
                        onClick={() => handleCancel(req.id)}
                        disabled={cancellingId === req.id}
                        className="px-4 py-2.5 rounded-xl text-xs font-bold text-gray-500 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        {cancellingId === req.id ? '취소중...' : '요청 취소'}
                      </button>
                    </div>
                  )}

                  {req.status === 'matched' && (
                    <button
                      onClick={() => navigate('/app/matching')}
                      className="w-full py-2.5 rounded-xl text-xs font-bold text-[#2E7D6B] bg-[#E8F5E9] cursor-pointer hover:bg-[#C8E6C9] transition-colors whitespace-nowrap"
                    >
                      <i className="ri-file-list-3-line mr-1" />
                      매칭 내용 보기
                    </button>
                  )}

                  {req.status === 'completed' && (
                    <div className="w-full py-2.5 rounded-xl text-xs font-bold text-[#1976D2] bg-blue-50 text-center">
                      <i className="ri-check-double-line mr-1" />
                      간병이 완료되었습니다
                    </div>
                  )}

                  {req.status === 'cancelled' && (
                    <div className="w-full py-2.5 rounded-xl text-xs font-bold text-gray-400 bg-gray-50 text-center">
                      취소된 요청입니다
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}